import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import Footer from '../Footer';
import PageLoader from '../ui/PageLoader';
import PageBackLink from '../ui/PageBackLink';
import EmptyState from '../ui/EmptyState';

type TicketRow = {
  id: number;
  status: string;
  event_title?: string;
  event_slug?: string;
  ticket_type_name?: string;
  token?: string | null;
  token_last4?: string | null;
  holder_name?: string | null;
  issued_at?: string | null;
};

const MyTicketsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [flagEnabled, setFlagEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        if (!apiService.isAuthenticated()) {
          setError('Sign in to view your tickets.');
          return;
        }
        const data = await apiService.getMyTickets();
        setFlagEnabled(data.flag_enabled);
        setTickets((data.tickets || []) as TicketRow[]);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not load tickets');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <>
        <PageLoader message="Loading tickets…" />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <PageBackLink label="Back to events" to="/events" className="mb-4" />
        <h1 className="heading-display mb-2">My tickets</h1>
        <p className="text-muted text-sm mb-6">
          Spectator tickets you purchased. Show the code at the gate. Email delivery is not available.
        </p>

        {error ? (
          <EmptyState variant="dense" title="Tickets unavailable" description={error} />
        ) : !flagEnabled ? (
          <EmptyState
            variant="dense"
            title="Spectator tickets not enabled"
            description="When ticketing is turned on for an event, purchased tickets appear here."
          />
        ) : tickets.length === 0 ? (
          <EmptyState variant="dense" title="No tickets yet" description="Buy spectator tickets from an event page when available." />
        ) : (
          <ul className="space-y-3">
            {tickets.map((ticket) => (
              <li key={ticket.id} className="rounded-xl border border-white/[0.06] bg-[#0c0c0c] p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">
                      {ticket.event_slug ? (
                        <Link to={`/events/${ticket.event_slug}`} className="hover:text-orange-400">
                          {ticket.event_title || 'Event'}
                        </Link>
                      ) : (
                        ticket.event_title || 'Event'
                      )}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {ticket.ticket_type_name || 'Ticket'} · {ticket.status}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">#{ticket.id}</p>
                </div>
                {ticket.token ? (
                  <p className="mt-3 font-mono text-sm break-all text-orange-200" aria-label="Ticket code">
                    {ticket.token}
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-gray-500">Code ending {ticket.token_last4 || '····'}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyTicketsPage;
